import { useState } from 'react';
import './ResolutionPrompt.css';

interface ResolutionPromptProps {
  onConfirmResolved: () => void;
  onReopen: () => void;
}

/**
 * ResolutionPrompt - Clear resolution and closure flow
 *
 * When a ticket is resolved:
 * - Confirmation question: "Did this solve your problem?"
 * - Yes: Closes ticket and navigates away
 * - No: Reopens ticket for further assistance
 */
export function ResolutionPrompt({ onConfirmResolved, onReopen }: ResolutionPromptProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'yes' | 'no' | null>(null);

  const handleYes = async () => {
    setIsProcessing(true);
    setSelectedOption('yes');
    // Small delay for visual feedback
    await new Promise((resolve) => setTimeout(resolve, 300));
    onConfirmResolved();
  };

  const handleNo = async () => {
    setIsProcessing(true);
    setSelectedOption('no');
    // Small delay for visual feedback
    await new Promise((resolve) => setTimeout(resolve, 300));
    onReopen();
    setIsProcessing(false);
    setSelectedOption(null);
  };

  return (
    <div className="resolution-prompt">
      <div className="resolution-prompt__icon">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="20" stroke="#107c10" strokeWidth="2.5" />
          <path
            d="M14 24l7 7 13-14"
            stroke="#107c10"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="resolution-prompt__content">
        <h2 className="resolution-prompt__title">This request has been marked as resolved</h2>
        <p className="resolution-prompt__question">Did this solve your problem?</p>

        <div className="resolution-prompt__actions">
          <button
            type="button"
            className={`resolution-prompt__btn resolution-prompt__btn--yes ${selectedOption === 'yes' ? 'resolution-prompt__btn--selected' : ''}`}
            onClick={handleYes}
            disabled={isProcessing}
          >
            {selectedOption === 'yes' && isProcessing ? (
              <>
                <div className="resolution-prompt__spinner" />
                Confirming...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 9l4 4 6-8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Yes, all resolved
              </>
            )}
          </button>

          <button
            type="button"
            className={`resolution-prompt__btn resolution-prompt__btn--no ${selectedOption === 'no' ? 'resolution-prompt__btn--selected' : ''}`}
            onClick={handleNo}
            disabled={isProcessing}
          >
            {selectedOption === 'no' && isProcessing ? (
              <>
                <div className="resolution-prompt__spinner" />
                Reopening...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 5l8 8M13 5l-8 8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                No, I still need help
              </>
            )}
          </button>
        </div>

        <p className="resolution-prompt__hint">
          If you select "No", we'll reopen this request and an IT team member will follow up with you.
        </p>
      </div>
    </div>
  );
}
